import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import axios from "axios";

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }

  return context;
};

function formatApiError(error) {
  if (!error?.response) {
    return "Backend nicht erreichbar. Bitte Verbindung oder Server prüfen.";
  }

  const detail = error?.response?.data?.detail;

  if (detail == null) {
    return error?.message || "Etwas ist schiefgelaufen. Bitte erneut versuchen.";
  }

  if (typeof detail === "string") {
    return detail;
  }

  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        if (item && typeof item.msg === "string") return item.msg;
        return JSON.stringify(item);
      })
      .filter(Boolean)
      .join(" ");
  }

  if (detail && typeof detail.msg === "string") {
    return detail.msg;
  }

  return String(detail);
}

function getBackendUrl() {
  const envUrl = process.env.REACT_APP_BACKEND_URL || "";

  if (envUrl.trim()) {
    return envUrl.replace(/\/$/, "");
  }

  if (window.location.hostname === "localhost") {
    return "http://localhost:8000";
  }

  return "";
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const BACKEND_URL = useMemo(() => getBackendUrl(), []);
  const API = useMemo(() => `${BACKEND_URL}/api`, [BACKEND_URL]);

  useEffect(() => {
    axios.defaults.withCredentials = true;
  }, []);

  const checkAuth = useCallback(async () => {
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const { data } = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
      });

      setUser(data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(
        `${API}/auth/login`,
        {
          email: email.trim().toLowerCase(),
          password,
        },
        {
          withCredentials: true,
        }
      );

      setUser(data);

      return {
        success: true,
      };
    } catch (error) {
      console.error("Login failed:", error);

      setUser(null);

      return {
        success: false,
        error: formatApiError(error),
      };
    }
  };

  const register = async (email, password, name, username) => {
    try {
      const { data } = await axios.post(
        `${API}/auth/register`,
        {
          email: email.trim().toLowerCase(),
          password,
          name: name.trim(),
          username: username.trim(),
        },
        {
          withCredentials: true,
        }
      );

      setUser(data);

      return {
        success: true,
      };
    } catch (error) {
      console.error("Register failed:", error);

      setUser(null);

      return {
        success: false,
        error: formatApiError(error),
      };
    }
  };

  const logout = async () => {
    try {
      await axios.post(
        `${API}/auth/logout`,
        {},
        {
          withCredentials: true,
        }
      );
    } catch (error) {
      console.error("Logout failed:", error);
    } finally {
      setUser(null);
    }
  };

  const value = useMemo(
    () => ({
      user,
      setUser,
      loading,
      login,
      register,
      logout,
      checkAuth,
      isAuthenticated: Boolean(user),
      apiBaseUrl: API,
    }),
    [user, loading, API, checkAuth]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};